import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { MeResponseSchema, type MeResponse } from '../../shared/auth.ts';

// Single source of truth for the React Query cache key. Anything that reads
// or mutates auth state (useMe, logout, login redirect callers) must use this
// constant so invalidations actually hit the same cache entry.
export const ME_QUERY_KEY = ['me'] as const;

// `null` is the explicit logged-out signal returned by the query. Callers
// branch on `data === undefined` (still loading) vs `data === null`
// (anonymous) vs `data` (authenticated) — see Home in routes/index.tsx.
export type MeQueryData = MeResponse | null;

/**
 * Fetch `/api/me` once, mapping the unauthenticated 401 response to `null`.
 *
 * `useMe()` is the only sanctioned way to read the owner's auth status on the
 * client. The server returns `401 { error: 'Unauthorized' }` when the session
 * cookie is missing/invalid/expired; we treat that as a normal "logged out"
 * state, not an error, so React Query does not retry it and the consuming UI
 * can render a login screen instead of a generic error.
 *
 * Any other non-2xx response (5xx, malformed JSON, network failure) is
 * surfaced as a query error so the UI can degrade gracefully — in Phase 1
 * the Home route conservatively treats those as logged-out as well so the
 * owner can click `Sign in with GitHub` and recover.
 */
export function useMe(): UseQueryResult<MeQueryData> {
  return useQuery<MeQueryData>({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    // The signed cookie is the long-lived source of truth; we revalidate on
    // tab focus so a manual logout in another tab is reflected here.
    refetchOnWindowFocus: true,
    // 401 is not an error the user can fix by retrying — surface it once.
    retry: false,
    // Keep the cached `me` for the lifetime of the tab; explicit
    // invalidations (logout, login callback) force a refetch.
    staleTime: Infinity,
  });
}

async function fetchMe(): Promise<MeQueryData> {
  const response = await fetch('/api/me', {
    method: 'GET',
    // The session cookie is HttpOnly + same-origin; `same-origin` is the
    // narrowest setting that still includes it on this fetch.
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });

  if (response.status === 401) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`GET /api/me failed with status ${response.status}`);
  }

  const payload: unknown = await response.json();
  // Parse on the way in so a server-side contract drift is caught at the
  // client boundary instead of crashing a downstream component.
  return MeResponseSchema.parse(payload);
}

/**
 * POST `/auth/logout` and invalidate the cached `me` query so the UI flips
 * back to the logged-out state. The server is idempotent — clearing the
 * cookie + DB row even when none exists — so we do not need to gate the
 * request on the current auth state.
 */
export function useLogout(): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      const response = await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { accept: 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`POST /auth/logout failed with status ${response.status}`);
      }
    },
    // `onSettled` (not `onSuccess`) is intentional (T6-R1-03): even if
    // POST /auth/logout fails, we still want to re-check auth state via
    // /api/me rather than leave the UI assuming the user is still
    // authenticated. The server is the source of truth for the cookie,
    // so a re-fetch on failure is the safer fail-mode.
    onSettled: async () => {
      // Force a refetch of /api/me so the auth-aware UI re-renders against
      // the server's view (cookie gone → 401 → null) instead of trusting an
      // optimistic local guess.
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}
